import type {AssigneePickerBaseAssignee} from '@github-ui/item-picker-assignee'
import {DOCUMENT_FIELDS_PATH, type SetFieldsOperation} from '@github-ui/optimistic-connection'
import {
  createConnectionMutationId,
  dispatchConnectionMutation,
} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  replaceActorsForAssignableRelayMutation,
  replaceActorsForAssignableRelayMutation$data,
  replaceActorsForAssignableRelayMutation$rawResponse,
} from './__generated__/replaceActorsForAssignableRelayMutation.graphql'

export type AssigneePickerAssignee = AssigneePickerBaseAssignee & {
  type?: string
}

type AgentAssignment = NonNullable<
  NonNullable<replaceActorsForAssignableRelayMutation$data['replaceActorsForAssignable']>['assignable']
>['agentAssignments']

type AssignedActor = {
  __typename?: string
  id?: string
  login?: string
  avatarUrl?: string
  isCopilot?: boolean | null
  isAgent?: boolean | null
}

/** Response nodes come from an `... on Actor` inline fragment, so every field is optional. */
function hasActorIdentity(
  actor: AssignedActor | null | undefined,
): actor is AssignedActor & {id: string; login: string; avatarUrl: string} {
  return actor?.id != null && actor.login != null && actor.avatarUrl != null
}

/**
 * Two representations: this mutation writes `assignedActors { nodes }`, rows render
 * `assignees { totalCount, edges { node } }`, and a consumer only takes keys its node already has.
 *
 * `assignees` is a `UserConnection`, so `User` only — keyed off `__typename` rather than
 * `isCopilot`/`isAgent`, or a Bot that is neither lands in a connection that can't hold it.
 */
export function toAssigneesOperation(
  issueId: string,
  assignedActors: ReadonlyArray<AssignedActor | null | undefined>,
): SetFieldsOperation {
  const actors = assignedActors.filter(hasActorIdentity)
  const users = actors.filter(actor => actor.__typename === 'User')
  return {
    type: 'setFields',
    id: issueId,
    fields: {
      assignedActors: {
        nodes: actors.map(actor => ({id: actor.id, login: actor.login, avatarUrl: actor.avatarUrl})),
      },
      assignees: {
        totalCount: users.length,
        edges: users.map(user => ({node: {id: user.id, login: user.login, avatarUrl: user.avatarUrl}})),
      },
    },
  }
}

export function buildOptimisticResponse(
  issueId: string,
  assignedActors: AssigneePickerAssignee[],
  newParticipants: AssigneePickerAssignee[],
  existingAgentAssignments?: AgentAssignment,
): replaceActorsForAssignableRelayMutation$rawResponse {
  return {
    replaceActorsForAssignable: {
      assignable: {
        __typename: 'Issue',
        __isNode: 'Issue',
        id: issueId,
        assignedActors: {
          nodes: assignedActors.map(a => {
            if (a.__typename === 'Bot') {
              return {
                __typename: 'Bot',
                __isActor: 'Bot',
                __isNode: 'Bot',
                id: a.id,
                login: a.login,
                name: a.name ?? null,
                avatarUrl: a.avatarUrl,
                profileResourcePath: a.profileResourcePath ?? null,
                displayName: a.displayName ?? '',
                isCopilot: a.isCopilot || false,
                isAgent: a.isAgent || false,
              }
            } else {
              return {
                __typename: 'User',
                __isActor: 'User',
                __isNode: 'User',
                id: a.id,
                login: a.login,
                name: a.name ?? null,
                avatarUrl: a.avatarUrl,
                profileResourcePath: a.profileResourcePath ?? null,
              }
            }
          }),
        },
        agentAssignments: (existingAgentAssignments ?? []).map(a => ({
          assignee: a.assignee
            ? {
                id: a.assignee.id,
                login: a.assignee.login,
                avatarUrl: a.assignee.avatarUrl,
                isCopilot: a.assignee.isCopilot,
                isAgent: a.assignee.isAgent,
                displayName: a.assignee.displayName,
              }
            : null,
          assigner:
            a.assigner && a.assigner.id
              ? {
                  __typename: a.assigner.__typename,
                  __isActor: a.assigner.__typename,
                  __isNode: a.assigner.__typename,
                  id: a.assigner.id,
                  login: a.assigner.login,
                }
              : null,
        })),
        participants: {
          nodes: newParticipants.map(a => ({
            id: a.id,
            login: a.login,
            name: a.name ?? null,
            avatarUrl: a.avatarUrl,
            profileResourcePath: a.profileResourcePath ?? null,
          })),
        },
      },
    },
  }
}

export function commitUpdateIssueAssigneesMutation({
  environment,
  input: {issueId, assignedActors, participants},
  existingAgentAssignments,
  clientMutationId,
  onError,
  onCompleted,
}: {
  environment: Environment
  input: {issueId: string; assignedActors: AssigneePickerAssignee[]; participants: AssigneePickerAssignee[]}
  existingAgentAssignments?: AgentAssignment
  clientMutationId?: string
  onError?: (error: Error) => void
  onCompleted?: (response: replaceActorsForAssignableRelayMutation$data) => void
}) {
  // merge assigned actors and participants into one, unique by login and take the first 5
  // do not include copilot or agents because bots do not show up as participants on issues
  // (the participants field on Issue returns UserConnection, which only contains User types)
  const newParticipants = [
    ...new Map(
      [...assignedActors, ...participants].filter(a => !a.isCopilot && !a.isAgent).map(item => [item.login, item]),
    ).values(),
  ].slice(0, 5)
  const mutationId = createConnectionMutationId()

  const dispatchOperationError = (error: Error) => dispatchConnectionMutation({phase: 'error', mutationId, error})

  dispatchConnectionMutation({
    phase: 'optimistic',
    mutationId,
    ownerId: issueId,
    connectionPath: DOCUMENT_FIELDS_PATH,
    operation: toAssigneesOperation(issueId, assignedActors),
  })

  // we use the `replaceActorsForAssignable` mutation specifically instead of `editIssue` as this has more specific permission
  // check for assignments.
  let disposable
  try {
    disposable = commitMutation<replaceActorsForAssignableRelayMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation replaceActorsForAssignableRelayMutation($input: ReplaceActorsForAssignableInput!) @raw_response_type {
          replaceActorsForAssignable(input: $input) {
            assignable {
              ... on Issue {
                id
                assignedActors(first: 20) {
                  nodes {
                    ... on Actor {
                      __typename
                      id
                      login
                      name
                      profileResourcePath
                      avatarUrl(size: 64)
                      ... on Bot {
                        isCopilot
                        isAgent
                        displayName
                      }
                    }
                  }
                }
                agentAssignments {
                  assignee {
                    id
                    login
                    avatarUrl(size: 64)
                    isCopilot
                    isAgent
                    displayName
                  }
                  assigner {
                    __typename
                    ... on Node {
                      id
                    }
                    ... on Actor {
                      login
                    }
                  }
                }
                participants(first: 10) {
                  nodes {
                    id
                    login
                    name
                    profileResourcePath
                    avatarUrl(size: 64)
                  }
                }
              }
            }
          }
        }
      `,
      variables: {input: {assignableId: issueId, actorIds: assignedActors.map(a => a.id), clientMutationId}},
      optimisticResponse: buildOptimisticResponse(issueId, assignedActors, newParticipants, existingAgentAssignments),
      onError: error => {
        dispatchOperationError(error)
        if (onError) onError(error)
      },
      onUnsubscribe: () => {
        dispatchOperationError(new Error('Updating assignees was cancelled'))
      },
      onCompleted: (response, errors) => {
        const confirmedActors = response.replaceActorsForAssignable?.assignable?.assignedActors?.nodes
        if (!confirmedActors) {
          const error = new Error(errors?.[0]?.message ?? 'Failed to update assignees')
          dispatchOperationError(error)
          if (onError) onError(error)
          return
        }
        if (!confirmedActors.every(hasActorIdentity)) {
          // The write completed, but an incomplete collection cannot safely settle mirrored consumers.
          dispatchOperationError(new Error(errors?.[0]?.message ?? 'Failed to update assignees'))
          if (onCompleted) onCompleted(response)
          return
        }

        dispatchConnectionMutation({
          phase: 'completed',
          mutationId,
          ownerId: issueId,
          connectionPath: DOCUMENT_FIELDS_PATH,
          operation: toAssigneesOperation(issueId, confirmedActors),
        })
        if (onCompleted) onCompleted(response)
      },
    })
  } catch (error) {
    dispatchOperationError(error instanceof Error ? error : new Error('Failed to update assignees'))
    throw error
  }

  return disposable
}
